import React, { useState } from "react";

import { Calendar, Plus, Search, Clock, Users, Settings, User, MapPin, Edit, Trash2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Layout } from "@/components/Layout";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import AgendamentoModal from "@/components/AgendamentoModal";
import GerenciarRegrasModal from "@/components/GerenciarRegrasModal";
import GerenciarAgendamentosModal from "@/components/GerenciarAgendamentosModal";
import CriarAreaComumModal from "@/components/CriarAreaComumModal";
import EditarAreaComumModal from "@/components/EditarAreaComumModal";
import { useCommonAreaRules } from "@/hooks/useCommonAreaRules";
import { useCommonAreas } from "@/hooks/useCommonAreas";
import { useMyBookings, useAllBookings } from "@/hooks/useBookings";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { SafeComponentWrapper } from "@/components/SafeComponentWrapper";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Removed AgendamentosPorDataModal as it's no longer needed
const AreasComuns = () => {
  return (
    <SafeComponentWrapper componentName="AreasComuns">
      <AreasComunsContent />
    </SafeComponentWrapper>
  );
};

const AreasComunsContent = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const {
    profile
  } = useAuth();
  const {
    data: rules
  } = useCommonAreaRules();
  const {
    data: areas = [],
    isLoading: areasLoading,
    refetch
  } = useCommonAreas();
  const {
    data: myBookings = [],
    isLoading: bookingsLoading
  } = useMyBookings();
  const { data: allBookings = [] } = useAllBookings();
  
  // Permission checks - moved to the beginning to fix initialization error
  const canManageRules = profile?.role === 'sindico' || profile?.role === 'administracao';
  const canCreateBookings = profile?.role === 'morador' || profile?.role === 'sindico';
  const canManageBookings = profile?.role === 'sindico' || profile?.role === 'administracao';
  const canManageAreas = profile?.role === 'sindico' || profile?.role === 'administracao';
  
  const filteredAreas = areas.filter(area => area.name.toLowerCase().includes(searchTerm.toLowerCase()) || area.description.toLowerCase().includes(searchTerm.toLowerCase()));
  const getStatusColor = (status: string) => {
    switch (status) {
      case "available":
        return "bg-green-100 text-green-800 border-green-200";
      case "occupied":
        return "bg-red-100 text-red-800 border-red-200";
      case "maintenance":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };
  const getStatusLabel = (status: string) => {
    switch (status) {
      case "available":
        return "Disponível";
      case "occupied":
        return "Ocupado";
      case "maintenance":
        return "Manutenção";
      default:
        return "Indisponível";
    }
  };
  const getBookingStatusLabel = (status: string) => {
    switch (status) {
      case "confirmed":
        return "Confirmado";
      case "pending":
        return "Pendente";
      case "cancelled":
        return "Cancelado";
      case "rejected":
        return "Rejeitado";
      default:
        return status;
    }
  };
  const getBookingStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  const formatDate = (dateString: string) => {
    // Usar apenas a parte da data (YYYY-MM-DD) para evitar problemas de timezone
    const bookingDate = dateString.split('T')[0]; // Remove a parte do horário se existir
    const today = new Date().toLocaleDateString('en-CA'); // Formato YYYY-MM-DD no timezone local
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowDate = tomorrow.toLocaleDateString('en-CA'); // Formato YYYY-MM-DD no timezone local
    
    if (bookingDate === today) {
      return "Hoje";
    } else if (bookingDate === tomorrowDate) {
      return "Amanhã";
    } else {
      // Para outras datas, criar o objeto Date forçando timezone local
      const [year, month, day] = bookingDate.split('-').map(Number);
      const localDate = new Date(year, month - 1, day); // month é 0-indexed
      return localDate.toLocaleDateString('pt-BR', {
        weekday: 'long',
        day: 'numeric',
        month: 'short'
      });
    }
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  // Filtrar agendamentos para a data selecionada
  const bookingsForSelectedDate = selectedDate 
    ? (() => {
        // Combinar myBookings com allBookings para mostrar todos os agendamentos
        const allAvailableBookings = canManageBookings ? allBookings : myBookings;
        
        return allAvailableBookings.filter(booking => {
          const bookingDate = parseISO(booking.booking_date);
          return format(bookingDate, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
        });
      })()
    : [];

  // Verificar se uma data tem agendamentos para destacar no calendário
  const hasBookingsOnDate = (date: Date) => {
    const allAvailableBookings = canManageBookings ? allBookings : myBookings;
    return allAvailableBookings.some(booking => {
      const bookingDate = parseISO(booking.booking_date);
      return format(bookingDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd');
    });
  };

  const handleDeleteArea = async (areaId: string, areaName: string) => {
    try {
      const { error } = await supabase
        .from("common_areas")
        .delete()
        .eq("id", areaId);

      if (error) throw error;

      toast.success(`Área "${areaName}" excluída com sucesso!`);
      refetch();
    } catch (error) {
      console.error("Erro ao excluir área:", error);
      toast.error("Erro ao excluir área comum");
    }
  };

  if (areasLoading) {
    return <Layout>
        <div className="p-6">
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-500">Carregando áreas comuns...</p>
          </div>
        </div>
      </Layout>;
  }
  return <Layout>
      <div className="p-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Áreas Comuns</h1>
            <p className="text-gray-600 dark:text-gray-400">Agende e gerencie o uso das áreas de lazer</p>
          </div>
          <div className="flex gap-2">
            {canManageAreas && (
              <CriarAreaComumModal>
                <Button variant="outline" className="sm:w-auto w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Área
                </Button>
              </CriarAreaComumModal>
            )}
            {canManageBookings && <GerenciarAgendamentosModal>
                <Button variant="outline" className="sm:w-auto w-full">
                  <Settings className="h-4 w-4 mr-2" />
                  Gerenciar
                </Button>
              </GerenciarAgendamentosModal>}
            {canCreateBookings && <AgendamentoModal>
                <Button className="bg-blue-600 hover:bg-blue-700 sm:w-auto w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Agendamento
                </Button>
              </AgendamentoModal>}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input placeholder="Buscar áreas..." className="pl-10" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </div>
            </div>

            {/* Lista de Áreas Comuns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredAreas.map((area) => (
                <Card key={area.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{area.image_emoji || "🏢"}</span>
                        <div>
                          <CardTitle className="text-lg">{area.name}</CardTitle>
                          {area.description && (
                            <CardDescription className="mt-1">
                              {area.description}
                            </CardDescription>
                          )}
                        </div>
                      </div>
                      {canManageAreas && (
                        <div className="flex gap-1">
                          <EditarAreaComumModal area={area}>
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </EditarAreaComumModal>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Excluir Área Comum</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja excluir a área "{area.name}"? Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteArea(area.id, area.name)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className={getStatusColor(area.status)}>
                        {getStatusLabel(area.status)}
                      </Badge>
                      <div className="flex items-center text-sm text-gray-600">
                        <Users className="h-4 w-4 mr-1" />
                        <span>Máx. {area.max_capacity} pessoas</span>
                      </div>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{area.operating_hours}</span>
                    </div>
                    {canCreateBookings && area.status === 'available' && (
                      <AgendamentoModal>
                        <Button size="sm" className="w-full">
                          <Calendar className="h-4 w-4 mr-2" />
                          Agendar
                        </Button>
                      </AgendamentoModal>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Calendário na área principal */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5" />
                  <span>Calendário de Agendamentos</span>
                </CardTitle>
                <CardDescription>
                  Clique em uma data para visualizar os agendamentos
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    setSelectedDate(date);
                  }}
                  className={cn("rounded-md border p-3 pointer-events-auto cursor-pointer")}
                  modifiers={{
                    hasBookings: (date) => hasBookingsOnDate(date)
                  }}
                  modifiersStyles={{
                    hasBookings: {
                      backgroundColor: '#dbeafe',
                      color: '#1e40af',
                      fontWeight: 'bold'
                    }
                  }}
                  locale={ptBR}
                />
                <p className="text-xs text-gray-500">
                  Datas em azul possuem agendamentos. Clique em uma data para ver detalhes.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            {/* Agendamentos para a data selecionada */}
            {selectedDate && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-base">
                    {format(selectedDate, "dd/MM/yyyy", { locale: ptBR })}
                  </CardTitle>
                  {canCreateBookings && (
                    <AgendamentoModal>
                      <Button size="sm" className="text-xs">
                        <Plus className="h-3 w-3 mr-1" />
                        Novo
                      </Button>
                    </AgendamentoModal>
                  )}
                </CardHeader>
                <CardContent className="space-y-3">
                  {bookingsForSelectedDate.length > 0 ? (
                    <div className="space-y-2">
                      {bookingsForSelectedDate.map(booking => (
                        <div key={booking.id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-sm">{booking.area_name}</h4>
                            <Badge variant="outline" className={`text-xs ${getBookingStatusColor(booking.status)}`}>
                              {getBookingStatusLabel(booking.status)}
                            </Badge>
                          </div>
                          <div className="space-y-1 text-xs text-gray-600">
                            <p className="flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                            </p>
                            <p className="flex items-center">
                              <User className="h-3 w-3 mr-1" />
                              {booking.profiles?.full_name || 'Usuário'}
                            </p>
                            {booking.notes && (
                              <p className="text-gray-500 mt-1">{booking.notes}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <p className="text-sm">Nenhum agendamento para esta data</p>
                      {canCreateBookings && (
                        <AgendamentoModal>
                          <Button variant="outline" size="sm" className="mt-2">
                            <Plus className="h-4 w-4 mr-2" />
                            Fazer Agendamento
                          </Button>
                        </AgendamentoModal>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {myBookings.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Meus Próximos Agendamentos</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    {myBookings.slice(0, 3).map(booking => (
                      <div key={booking.id} className="p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-xs">{booking.area_name}</h4>
                          <Badge variant="outline" className={`text-xs ${getBookingStatusColor(booking.status)}`}>
                            {getBookingStatusLabel(booking.status)}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600">{formatDate(booking.booking_date)} às {formatTime(booking.start_time)}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-base">Regras Importantes</CardTitle>
                {canManageRules && <GerenciarRegrasModal>
                    <Button variant="outline" size="sm">
                      <Settings className="h-4 w-4 mr-1" />
                      Gerenciar
                    </Button>
                  </GerenciarRegrasModal>}
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-gray-600">
                {rules && rules.length > 0 ? (
                  <div className="space-y-2">
                    {rules.map(rule => (
                      <p key={rule.id} className="flex items-start space-x-2 mb-2">
                        <span className="text-blue-500 mt-1 flex-shrink-0">•</span>
                        <span className="flex-1">{rule.rule_text}</span>
                      </p>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="flex items-start space-x-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Agendamentos podem ser feitos com até 7 dias de antecedência</span>
                    </p>
                    <p className="flex items-start space-x-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Máximo de 2 agendamentos por mês por apartamento</span>
                    </p>
                    <p className="flex items-start space-x-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>Cancelamentos devem ser feitos com 24h de antecedência</span>
                    </p>
                    <p className="flex items-start space-x-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>É necessário apresentar carteirinha do condomínio</span>
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>;
};

export default AreasComuns;